import { getSharedState } from '@frontend-platform/host-state';
import { useMemo } from 'react';
import { useSharedKey } from '@/shared/hooks/use-shared-key';
import {
  CALIBRATION_CONFIG_DEFAULTS,
  type CalibrationConfig,
  parseCalibrationConfig,
} from '../lib/calibration-config';

/**
 * Per-bot calibration limits, read from the host rather than hardcoded.
 *
 * aistudio publishes the whole `bots` Vuex module onto `@frontend-platform/host-state`
 * (client/app.js), so the `CALIBRATION_CONFIG` bot property and the AI
 * profile's timezone are both already there — no extra request needed, and the
 * remote can't drift from the caps the host enforces.
 *
 * Reactive via `useSharedKey('bots')` for the same reason
 * `use-is-dynamic-env` is: the host's bootstrap is async, so a one-time
 * read can land before the bot is published and lock in defaults for the whole
 * mount. A bot switch re-publishes `bots` and re-reads here.
 */

// A bot property as aistudio stores it: `{ value: <serialized> }`.
type BotProperties = Record<string, { value?: unknown } | undefined>;

// The raw property value, not the parsed object: the snapshot must be
// reference-stable between publishes, and parsing here would hand back a fresh
// object every render and loop forever.
function getRawConfigSnapshot(): unknown {
  // `currentBot` is loosely typed on SharedVuexState (an index signature), so
  // `properties` arrives as `unknown` and has to be narrowed here.
  const properties = getSharedState()?.bots?.currentBot?.properties as BotProperties | undefined;
  return properties?.CALIBRATION_CONFIG?.value;
}

function getTimezoneSnapshot(): string {
  const aiProfile = getSharedState()?.bots?.aiProfile as { timezone?: string } | undefined;
  return aiProfile?.timezone || 'UTC';
}

export function useCalibrationConfig(): {
  calibrationConfig: CalibrationConfig;
  timezone: string;
} {
  const raw = useSharedKey('bots', getRawConfigSnapshot);
  const timezone = useSharedKey('bots', getTimezoneSnapshot);

  // Parse only when the raw value actually changes, so consumers get a stable
  // object they can safely put in dependency arrays.
  const calibrationConfig = useMemo(() => parseCalibrationConfig(raw), [raw]);

  return { calibrationConfig, timezone };
}

export { CALIBRATION_CONFIG_DEFAULTS };
export type { CalibrationConfig };
